import React, { useEffect, useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  FormContainer,
  FormCard,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  FormGroup,
  Label,
  Input,
  TextArea,
  DropZone,
  DropZoneContent,
  FilePreview,
  FileInfo,
  Button,
  RemoveButton,
  RecaptchaGroup,
  PositionContainer,
  PlatformLabel,
  RowContainer,
  Spacer,
  RedAsterisk,
  ErrorText,
  ErrorContainer,
  SuccessText,
  SuccessContainer,
  ClearButton,
} from '../styles/form.styles';
import { Upload, X, FileText, CheckCircle, Square, SquareCheck, TriangleAlert, CircleCheckBig } from 'lucide-react';
import { useToast } from './ui/toast';
import { BannerService } from 'services';

const SUPPORTED_IMAGE_TYPE = ['image/gif', 'image/png', 'image/jpg', 'image/jpeg'];

const PC_BANNER_LEFT = 'pc_left';
const PC_BANNER_RIGHT = 'pc_right';
const MOBILE_BANNER_LEFT = 'mobile_left';
const MOBILE_BANNER_RIGHT = 'mobile_right';

export default function BannerContactForm() {
  const recaptchaRef = useRef(null);

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const [reCaptcha, setReCaptcha] = useState('');
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [errorPositionMessage, setErrorPositionMessage] = useState('');
  const [errorEmailMessage, setErrorEmailMessage] = useState('');
  const [errorFileMessage, setErrorFileMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fileInvalidTimeoutRef = useRef(null);

  useEffect(() => {
    if (successMessage) {
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorFileMessage) {
      fileInvalidTimeoutRef.current = setTimeout(() => {
        setErrorFileMessage('');
      }, 5000);
    }

    return () => {
      clearTimeout(fileInvalidTimeoutRef.current);
    };
  }, [errorFileMessage]);

  useEffect(() => {
    if (selectedPositions.length > 0) {
      setErrorPositionMessage('');
    }
  }, [selectedPositions]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      validateAndSetFile(droppedFile);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSetFile(e.target.files[0]);
    }

    e.target.value = '';
  };

  const validateAndSetFile = (file) => {
    const fileType = file.type;
    if (
      fileType !== 'image/gif' &&
      fileType !== 'image/png' &&
      fileType !== 'image/jpg' &&
      fileType !== 'image/jpeg' &&
      fileType !== 'video/mp4' &&
      fileType !== 'application/zip' &&
      fileType !== 'application/x-zip-compressed'
    ) {
      setErrorFileMessage('이미지, MP4 또는 ZIP 파일만 업로드 가능합니다.');
      toast({
        title: '파일 형식 오류',
        description: '이미지, MP4 또는 ZIP 파일만 업로드 가능합니다.',
        variant: 'destructive',
      });
      return;
    }

    let maxSize;
    if (SUPPORTED_IMAGE_TYPE.includes(fileType)) {
      maxSize = 1.5 * 1024 * 1024;
    } else if (fileType === 'video/mp4') {
      maxSize = 4 * 1024 * 1024;
    } else if (fileType === 'application/zip' || fileType === 'application/x-zip-compressed') {
      maxSize = 10 * 1024 * 1024;
    }

    if (file.size > maxSize) {
      let errorMessage;
      if (SUPPORTED_IMAGE_TYPE.includes(fileType)) {
        errorMessage = '이미지 파일은 1.5MB 이하여야 합니다.';
      } else if (fileType === 'video/mp4') {
        errorMessage = 'MP4 파일은 4MB 이하여야 합니다.';
      } else {
        errorMessage = 'ZIP 파일은 10MB 이하여야 합니다.';
      }

      setErrorFileMessage(errorMessage);
      toast({
        title: '파일 크기 초과',
        description: errorMessage,
        variant: 'destructive',
      });
      return;
    }

    setErrorFileMessage('');
    setFile(file);
  };

  const removeFile = () => {
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setErrorEmailMessage('이메일을 입력하세요.');
      toast({
        title: '정보 누락',
        description: '이메일을 입력하세요.',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedPositions.length) {
      setErrorPositionMessage('최소한 하나 이상의 위치를 선택하세요.');
      toast({
        title: '정보 누락',
        description: '최소한 하나 이상의 위치를 선택하세요.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);

    try {
      const form = new FormData();
      form.append('email', email);
      form.append('content', message);
      form.append('device', 'WEB');
      form.append('captcha', reCaptcha);
      form.append('file', file);
      form.append('position', selectedPositions.join(','));

      const response = await BannerService.submitSportBannerRequest(form);
      if (response.code !== 200) {
        let message = response.message ?? '나중에 다시 시도해주세요.';
        setErrorMessage(message);
        toast({
          title: '제출 중 오류 발생',
          description: message,
          variant: 'destructive',
        });
        setIsUploading(false);
        return;
      }

      setSuccessMessage('문의가 성공적으로 제출되었습니다. 네임드팀에서 곧 연락드리겠습니다.');
      toast({
        title: '문의가 성공적으로 제출되었습니다',
        description: '네임드팀에서 곧 연락드리겠습니다.',
        variant: 'default',
      });

      setEmail('');
      setMessage('');
      setFile(null);
      setSelectedPositions([]);
      setReCaptcha('');
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      toast({
        title: '제출 중 오류 발생',
        description: '나중에 다시 시도해주세요.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const onReCaptchaChange = (value) => {
    setReCaptcha(value);
  };

  const onSelectPcBanners = () => {
    if (selectedPositions.includes(PC_BANNER_LEFT) && selectedPositions.includes(PC_BANNER_RIGHT)) {
      const positions = selectedPositions.filter(
        (position) => position !== PC_BANNER_LEFT && position !== PC_BANNER_RIGHT
      );
      setSelectedPositions(positions);
    } else {
      const positions = Array.from(new Set([...selectedPositions, PC_BANNER_LEFT, PC_BANNER_RIGHT]));
      setSelectedPositions(positions);
    }
  };

  const onSelectMobileBanners = () => {
    if (selectedPositions.includes(MOBILE_BANNER_LEFT) && selectedPositions.includes(MOBILE_BANNER_RIGHT)) {
      const positions = selectedPositions.filter(
        (position) => position !== MOBILE_BANNER_LEFT && position !== MOBILE_BANNER_RIGHT
      );
      setSelectedPositions(positions);
    } else {
      const positions = Array.from(new Set([...selectedPositions, MOBILE_BANNER_LEFT, MOBILE_BANNER_RIGHT]));
      setSelectedPositions(positions);
    }
  };

  const onSelectMobileLeft = () => {
    if (selectedPositions.includes(MOBILE_BANNER_LEFT)) {
      const positions = selectedPositions.filter((position) => position !== MOBILE_BANNER_LEFT);
      setSelectedPositions(positions);
    } else {
      setSelectedPositions([...selectedPositions, MOBILE_BANNER_LEFT]);
    }
  };

  const onSelectMobileRight = () => {
    if (selectedPositions.includes(MOBILE_BANNER_RIGHT)) {
      const positions = selectedPositions.filter((position) => position !== MOBILE_BANNER_RIGHT);
      setSelectedPositions(positions);
    } else {
      setSelectedPositions([...selectedPositions, MOBILE_BANNER_RIGHT]);
    }
  };

  const onSelectPcLeft = () => {
    if (selectedPositions.includes(PC_BANNER_LEFT)) {
      const positions = selectedPositions.filter((position) => position !== PC_BANNER_LEFT);
      setSelectedPositions(positions);
    } else {
      setSelectedPositions([...selectedPositions, PC_BANNER_LEFT]);
    }
  };

  const onSelectPcRight = () => {
    if (selectedPositions.includes(PC_BANNER_RIGHT)) {
      const positions = selectedPositions.filter((position) => position !== PC_BANNER_RIGHT);
      setSelectedPositions(positions);
    } else {
      setSelectedPositions([...selectedPositions, PC_BANNER_RIGHT]);
    }
  };

  const emailInputRef = useRef(null);
  const positionInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (errorEmailMessage && emailInputRef.current) {
      emailInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errorEmailMessage]);

  useEffect(() => {
    if (errorPositionMessage && positionInputRef.current) {
      positionInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errorPositionMessage]);

  useEffect(() => {
    if (errorFileMessage && fileInputRef.current) {
      fileInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errorFileMessage]);

  return (
    <FormContainer>
      <FormCard>
        <CardHeader>
          <CardTitle>네임드팀에 문의하기</CardTitle>
          <CardDescription>
            네임드 광고 배너 임대 양식을 작성하여 제출해주시면 최대한 신속하게 처리해 드리겠습니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor='email'>
                이메일 주소<RedAsterisk>*</RedAsterisk>
              </Label>
              <Input
                id='email'
                type='email'
                placeholder='이메일을 입력하세요...'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                ref={emailInputRef}
              />
              {errorEmailMessage && (
                <ErrorContainer>
                  <TriangleAlert size={18} color='#ef4444' />
                  <ErrorText>{errorEmailMessage}</ErrorText>
                  <ClearButton type='button' onClick={() => setErrorEmailMessage('')}>
                    <X size={16} />
                  </ClearButton>
                </ErrorContainer>
              )}
            </FormGroup>
            <FormGroup ref={positionInputRef}>
              <Label>
                배너 위치 선택 (최소 1개 이상 선택 필요)<RedAsterisk>*</RedAsterisk>
              </Label>
              <RowContainer onClick={onSelectPcBanners}>
                <Checkbox
                  isChecked={selectedPositions.includes(PC_BANNER_LEFT) || selectedPositions.includes(PC_BANNER_RIGHT)}
                />
                <PlatformLabel>스포츠 응원하기 배너 (PC)</PlatformLabel>
              </RowContainer>
              <PositionContainer>
                <RowContainer onClick={onSelectPcRight}>
                  <Checkbox isChecked={selectedPositions.includes(PC_BANNER_RIGHT)} />
                  <PlatformLabel>우측</PlatformLabel>
                </RowContainer>
                <RowContainer onClick={onSelectPcLeft}>
                  <Checkbox isChecked={selectedPositions.includes(PC_BANNER_LEFT)} />
                  <PlatformLabel>좌측</PlatformLabel>
                </RowContainer>
              </PositionContainer>

              <Spacer />

              <RowContainer onClick={onSelectMobileBanners}>
                <Checkbox
                  isChecked={
                    selectedPositions.includes(MOBILE_BANNER_LEFT) || selectedPositions.includes(MOBILE_BANNER_RIGHT)
                  }
                />
                <PlatformLabel>스포츠 응원하기 배너 (모바일)</PlatformLabel>
              </RowContainer>
              <PositionContainer>
                <RowContainer onClick={onSelectMobileRight}>
                  <Checkbox isChecked={selectedPositions.includes(MOBILE_BANNER_RIGHT)} />
                  <PlatformLabel>우측</PlatformLabel>
                </RowContainer>
                <RowContainer onClick={onSelectMobileLeft}>
                  <Checkbox isChecked={selectedPositions.includes(MOBILE_BANNER_LEFT)} />
                  <PlatformLabel>좌측</PlatformLabel>
                </RowContainer>
              </PositionContainer>

              {errorPositionMessage && (
                <ErrorContainer>
                  <TriangleAlert size={18} color='#ef4444' />
                  <ErrorText>{errorPositionMessage}</ErrorText>
                  <ClearButton type='button' onClick={() => setErrorPositionMessage('')}>
                    <X size={16} />
                  </ClearButton>
                </ErrorContainer>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor='message'>요청 내용 입력</Label>
              <TextArea
                id='message'
                placeholder='해당 배너 임대에 대한 요청 사항을 입력해 주세요.'
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </FormGroup>

            <FormGroup ref={fileInputRef}>
              <Label>배너 파일 업로드 (이미지, MP4 또는 ZIP 파일)</Label>
              <DropZone
                isDragging={isDragging}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {!file ? (
                  <DropZoneContent>
                    <Upload size={40} />
                    <div>
                      <p className='dropzone-text'>파일을 여기에 끌어다 놓거나 클릭하여 찾아보기</p>
                      <p className='dropzone-subtext'>이미지 (최대 1.5MB), MP4 (최대 4MB) 또는 ZIP (최대 10MB)</p>
                    </div>
                    <input
                      id='file-upload'
                      type='file'
                      className='hidden-input'
                      accept='image/*,.mp4,.zip'
                      onChange={handleFileChange}
                    />
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => document.getElementById('file-upload')?.click()}
                    >
                      파일 선택
                    </Button>
                  </DropZoneContent>
                ) : (
                  <FilePreview>
                    <div className='file-header'>
                      <FileText size={24} />
                      <span className='file-name'>{file.name}</span>
                      <RemoveButton type='button' onClick={removeFile}>
                        <X size={16} />
                      </RemoveButton>
                    </div>
                    <FileInfo>{(file.size / (1024 * 1024)).toFixed(2)} MB</FileInfo>
                    <div className='file-status'>
                      <CheckCircle size={20} className='success-icon' />
                      <span className='success-text'>파일 업로드 완료</span>
                    </div>
                  </FilePreview>
                )}
              </DropZone>
              {errorFileMessage && (
                <ErrorContainer>
                  <TriangleAlert size={18} color='#ef4444' />
                  <ErrorText>{errorFileMessage}</ErrorText>
                  <ClearButton
                    type='button'
                    onClick={() => {
                      if (fileInvalidTimeoutRef.current) {
                        clearTimeout(fileInvalidTimeoutRef.current);
                      }
                      setErrorFileMessage('');
                    }}
                  >
                    <X size={16} />
                  </ClearButton>
                </ErrorContainer>
              )}
            </FormGroup>

            {errorMessage && (
              <ErrorContainer>
                <TriangleAlert size={18} color='#ef4444' />
                <ErrorText>{errorMessage}</ErrorText>
                <ClearButton type='button' onClick={() => setErrorMessage('')}>
                  <X size={16} />
                </ClearButton>
              </ErrorContainer>
            )}

            {successMessage && (
              <SuccessContainer>
                <CircleCheckBig size={18} color='#10b981' />
                <SuccessText>{successMessage}</SuccessText>
              </SuccessContainer>
            )}

            <RecaptchaGroup>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={'6LdU1fEqAAAAAGHmyawxwiw914Nt9jjnX8iBUZCh'}
                onChange={onReCaptchaChange}
                hl='ko'
              />
            </RecaptchaGroup>

            <Button type='submit' variant='primary' fullWidth disabled={isUploading || !reCaptcha || errorFileMessage}>
              {isUploading ? '제출 중...' : '문의 제출'}
            </Button>
          </form>
        </CardContent>
      </FormCard>
    </FormContainer>
  );
}

function Checkbox({ isChecked }) {
  const [checked, setChecked] = useState(isChecked);

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  return checked ? <SquareCheck color='#6b7280' size={20} /> : <Square color='#6b7280' size={20} />;
}
